// src/pages/retail-shops/SalesTerminal.jsx
import React, {
  useState, useEffect, useCallback, useMemo, useRef, Suspense,
} from 'react';
import axios from 'axios';
import {
  Box, Typography, Grid, Button, Chip, IconButton, Tooltip, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField,
  LinearProgress, Alert, Snackbar, InputAdornment, CircularProgress,
  Fade, Paper, Divider, Stack, Avatar, alpha, Zoom, Tabs, Tab,
} from '@mui/material';
import {
  Add as AddIcon, Delete as DeleteIcon, Search as SearchIcon,
  Clear as ClearIcon, Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Inventory as InventoryIcon,
  AddCircleOutline as AddCircleOutlineIcon,
  RemoveCircleOutline as RemoveCircleOutlineIcon,
  QrCodeScanner as QrCodeScannerIcon, CameraAlt as CameraAltIcon,
  LocalOffer as LocalOfferIcon, AttachMoney as AttachMoneyIcon,
  Receipt as ReceiptIcon, Keyboard as KeyboardIcon,
  FlashOn as FlashOnIcon, Calculate as CalculateIcon,
  PauseCircleOutline as HoldIcon, PlayCircleOutline as ResumeIcon,
  Print as PrintIcon,
  Refresh as RefreshIcon,
  AccountBalanceWallet as WalletIcon,
  PersonSearch as PersonSearchIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  Logout as LogoutIcon,
  Warning as WarningIcon,
  ExitToApp as ExitToAppIcon,
  ZoomIn as ZoomInIcon,
  ZoomOutMap as ZoomOutMapIcon,
} from '@mui/icons-material';

import '../../styles/Waiters.css';
import Sidebar from '../../components/tuck_shop/Sidebar';
import Header from '../../components/tuck_shop/Header';
import Footer from '../../components/tuck_shop/Footer';
import { ACCE_BRAND, glossyButton } from '../../theme/colors';

// ════════════════════════════════════════════
// ORG
// ════════════════════════════════════════════
const ORG = {
  name: process.env.REACT_APP_ORGANISATION_NAME || 'ACCE Tuckshop',
  address: process.env.REACT_APP_RESTAURANT_ADDRESS || '',
  phone: process.env.REACT_APP_RESTAURANT_PHONE || '',
  email: process.env.REACT_APP_RESTAURANT_EMAIL || '',
  website: process.env.REACT_APP_RESTAURANT_WEBSITE || '',
  city: process.env.REACT_APP_RESTAURANT_CITY || '',
  state: process.env.REACT_APP_RESTAURANT_STATE || '',
  country: process.env.REACT_APP_RESTAURANT_COUNTRY || '',
  hours: process.env.REACT_APP_RESTAURANT_OPERATING_HOURS || '',
  supportEmail: process.env.REACT_APP_RESTAURANT_SUPPORT_EMAIL || '',
  supportPhone: process.env.REACT_APP_RESTAURANT_SUPPORT_PHONE || '',
};

const COLORS = {
  elegantRed:     ACCE_BRAND.orange,
  elegantRedDark: ACCE_BRAND.orangeDark,
  navy:           ACCE_BRAND.navy,
  orange:         ACCE_BRAND.orange,
  orangeDark:     ACCE_BRAND.orangeDark,
  orangeLight:    ACCE_BRAND.orangeLight,
};

const TUCKSHOP = {
  id: 1,
  name: 'School Tuckshop',
  icon: '🏪',
};

const CART_STORAGE_KEY = 'tuckshop_pos_cart_v1';
const SESSION_STORAGE_KEY = 'tuckshop_pos_session_v1';

// ════════════════════════════════════════════
// API CLIENT
// ════════════════════════════════════════════
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000';
const API_PREFIX   = process.env.REACT_APP_API_PREFIX   || '/api';
const FULL_API_URL = `${API_BASE_URL}${API_PREFIX}`;

const api = axios.create({
  baseURL: FULL_API_URL,
  headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
  timeout: 30000,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const extractError = (err) => {
  const d = err?.response?.data;
  if (!d) return err.message || 'Request failed';
  if (typeof d === 'string') return d;
  if (d.errors) {
    const first = Object.values(d.errors)[0];
    return Array.isArray(first) ? first[0] : String(first);
  }
  return d.message || 'Request failed';
};

// ── Products ──
const apiListProducts = async (params = {}) => {
  try {
    const { data } = await api.get('/tuckshop/products', { params });
    return { success: true, ...data };
  } catch (err) { return { success: false, error: extractError(err) }; }
};

// ── Sales ──
const apiCreateSale = async (payload) => {
  try {
    const { data } = await api.post('/tuckshop/sales', payload);
    return { success: true, sale: data };
  } catch (err) { return { success: false, error: extractError(err) }; }
};

const apiTodayStats = async () => {
  try {
    const { data } = await api.get('/tuckshop/sales/stats/today');
    return { success: true, stats: data };
  } catch (err) { return { success: false, error: extractError(err) }; }
};

// ── Wallet lookup ──
const apiLookupWallet = async (staffId) => {
  try {
    const { data } = await api.get('/tuckshop/wallets/lookup', {
      params: { staff_id: staffId },
    });

    const payload = data?.data && typeof data.data === 'object' ? data.data : data;
    const user    = payload?.user   ?? null;
    const wallet  = payload?.wallet ?? null;

    if (!user) {
      return { success: false, error: 'Lookup response missing user.' };
    }

    return { success: true, user, wallet };
  } catch (err) {
    if (err.response?.status === 404) {
      return { success: false, notFound: true, error: extractError(err) };
    }
    return { success: false, error: extractError(err) };
  }
};

// ════════════════════════════════════════════
// UTILITIES
// ════════════════════════════════════════════
const formatCurrency = (v) => {
  if (v === null || v === undefined || v === '') return `₦0.00`;
  return `₦${Number(v).toLocaleString('en-NG', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}`;
};

const safeNumber = (v, fallback = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

const round2 = (n) => Math.round(safeNumber(n) * 100) / 100;
const toMoneyString = (v) => (Math.round(safeNumber(v) * 100) / 100).toFixed(2);
const normalizeBarcode = (s) => String(s || '').replace(/[\s\r\n\t]/g, '').trim();

const getInitials = (name) => {
  if (!name) return '?';
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0]?.[0] || '';
  const second = parts.length > 1 ? (parts[parts.length - 1]?.[0] || '') : '';
  return (first + second).toUpperCase() || '?';
};

// ════════════════════════════════════════════
// SESSION STORAGE HELPERS
// ════════════════════════════════════════════
const saveSession = (walletInfo) => {
  try {
    if (walletInfo) {
      sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(walletInfo));
    } else {
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
    }
  } catch (e) { /* ignore quota errors */ }
};

const loadSession = () => {
  try {
    const raw = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.user?.staff_id && parsed?.wallet) return parsed;
    return null;
  } catch (e) {
    return null;
  }
};

const saveCart = (cart) => {
  try {
    if (Array.isArray(cart) && cart.length > 0) {
      sessionStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cart));
    } else {
      sessionStorage.removeItem(CART_STORAGE_KEY);
    }
  } catch (e) { /* ignore */ }
};

const loadCart = () => {
  try {
    const raw = sessionStorage.getItem(CART_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
};

function computeTotals(cart) {
  let subtotal = 0;
  let discountTotal = 0;
  let taxTotal = 0;
  cart.forEach((line) => {
    const gross = safeNumber(line.unit_price, 0) * line.quantity;
    const disc  = safeNumber(line.line_discount, 0);
    const after = Math.max(0, gross - disc);
    const taxable = line.tax_included
      ? 0
      : round2(after * safeNumber(line.tax_rate, 0) / 100);
    subtotal += gross;
    discountTotal += disc;
    taxTotal += taxable;
  });
  const total = round2(subtotal - discountTotal + taxTotal);
  return {
    subtotal: round2(subtotal),
    discountTotal: round2(discountTotal),
    taxTotal: round2(taxTotal),
    total,
  };
}

// ════════════════════════════════════════════
// SHORTCUT / ROW
// ════════════════════════════════════════════
const Shortcut = ({ label, keys }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Chip size="small" label={keys} sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700, bgcolor: alpha(ACCE_BRAND.navy, 0.08), color: ACCE_BRAND.navy }} />
  </Box>
);

const Row = ({ label, value, bold, accent }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
    <Typography variant="body2" color={bold ? 'text.primary' : 'text.secondary'} fontWeight={bold ? 700 : 400}>
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={bold ? 800 : 600} color={accent ? COLORS.elegantRed : 'text.primary'}>
      {value}
    </Typography>
  </Box>
);

// ════════════════════════════════════════════
// CALCULATOR
// ════════════════════════════════════════════
const Calculator = ({ open, onClose, onSendToTender }) => {
  const [display, setDisplay] = useState('0');
  const [acc, setAcc] = useState(null);
  const [op, setOp] = useState(null);
  const [fresh, setFresh] = useState(true);
  const [memory, setMemory] = useState(0);
  const [pos, setPos] = useState({ x: 300, y: 200 });
  const dragRef = useRef({ dragging: false, offX: 0, offY: 0 });

  useEffect(() => {
    if (!open) return undefined;
    const move = (e) => {
      if (!dragRef.current.dragging) return;
      setPos({ x: e.clientX - dragRef.current.offX, y: e.clientY - dragRef.current.offY });
    };
    const up = () => { dragRef.current.dragging = false; };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    return () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
    };
  }, [open]);

  const onMouseDown = (e) => {
    dragRef.current = { dragging: true, offX: e.clientX - pos.x, offY: e.clientY - pos.y };
  };

  const inputDigit = (d) => {
    if (fresh) { setDisplay(String(d)); setFresh(false); }
    else setDisplay((s) => (s === '0' ? String(d) : s + d));
  };
  const inputDot = () => {
    if (fresh) { setDisplay('0.'); setFresh(false); }
    else if (!display.includes('.')) setDisplay(display + '.');
  };
  const clearAll = () => { setDisplay('0'); setAcc(null); setOp(null); setFresh(true); };
  const backspace = () => setDisplay((s) => (s.length > 1 ? s.slice(0, -1) : '0'));

  const compute = (a, b, o) => {
    const x = safeNumber(a, 0), y = safeNumber(b, 0);
    if (o === '+') return x + y;
    if (o === '-') return x - y;
    if (o === '×') return x * y;
    if (o === '÷') return y === 0 ? 0 : x / y;
    return y;
  };

  const applyOp = (newOp) => {
    const cur = safeNumber(display, 0);
    if (acc === null) setAcc(cur);
    else if (op && !fresh) {
      const r = compute(acc, cur, op);
      setAcc(r);
      setDisplay(String(round2(r)));
    }
    setOp(newOp);
    setFresh(true);
  };

  const equals = () => {
    if (op === null || acc === null) return;
    const r = compute(acc, safeNumber(display, 0), op);
    setDisplay(String(round2(r)));
    setAcc(null); setOp(null); setFresh(true);
  };

  const negate = () => setDisplay((s) => (s.startsWith('-') ? s.slice(1) : '-' + s));
  const percent = () => {
    const cur = safeNumber(display, 0);
    if (op && acc !== null) setDisplay(String(round2((acc * cur) / 100)));
    else setDisplay(String(round2(cur / 100)));
    setFresh(true);
  };

  if (!open) return null;

  const btn = (label, onClick, variant = 'default', flex = 1) => (
    <Button onClick={onClick} variant={variant === 'primary' ? 'contained' : 'outlined'}
      sx={{
        minWidth: 0, flex, py: 1.5, fontSize: '1rem', fontWeight: 700,
        bgcolor: variant === 'primary' ? COLORS.elegantRed : undefined,
        borderColor: variant === 'danger' ? '#d32f2f' : undefined,
        color: variant === 'danger' ? '#d32f2f' : undefined,
        '&:hover': { bgcolor: variant === 'primary' ? COLORS.elegantRedDark : undefined },
      }}>
      {label}
    </Button>
  );

  return (
    <Paper elevation={12} sx={{ position: 'fixed', top: pos.y, left: pos.x, width: 320, zIndex: 9999, borderRadius: 2, overflow: 'hidden', userSelect: 'none' }}>
      <Box onMouseDown={onMouseDown} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: ACCE_BRAND.navy, color: '#fff', p: 1, cursor: 'move' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CalculateIcon fontSize="small" />
          <Typography variant="subtitle2" fontWeight={700}>Calculator</Typography>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color: '#fff' }} aria-label="Close calculator">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <Box sx={{ p: 2 }}>
        <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5, textAlign: 'right', bgcolor: alpha('#000', 0.02) }}>
          <Typography variant="caption" color="text.secondary" display="block">
            {memory !== 0 ? `M: ${memory}` : '\u00A0'}
          </Typography>
          <Typography variant="h5" fontWeight={700} sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>{display}</Typography>
        </Paper>
        <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
          {btn('MC', () => setMemory(0))}
          {btn('MR', () => { setDisplay(String(memory)); setFresh(true); })}
          {btn('M+', () => setMemory((m) => round2(m + safeNumber(display, 0))))}
          {btn('M-', () => setMemory((m) => round2(m - safeNumber(display, 0))))}
        </Stack>
        <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
          {btn('C', clearAll, 'danger')}
          {btn('⌫', backspace)}
          {btn('%', percent)}
          {btn('÷', () => applyOp('÷'), 'primary')}
        </Stack>
        <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
          {btn('7', () => inputDigit(7))}
          {btn('8', () => inputDigit(8))}
          {btn('9', () => inputDigit(9))}
          {btn('×', () => applyOp('×'), 'primary')}
        </Stack>
        <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
          {btn('4', () => inputDigit(4))}
          {btn('5', () => inputDigit(5))}
          {btn('6', () => inputDigit(6))}
          {btn('−', () => applyOp('-'), 'primary')}
        </Stack>
        <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
          {btn('1', () => inputDigit(1))}
          {btn('2', () => inputDigit(2))}
          {btn('3', () => inputDigit(3))}
          {btn('+', () => applyOp('+'), 'primary')}
        </Stack>
        <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
          {btn('±', negate)}
          {btn('0', () => inputDigit(0))}
          {btn('.', inputDot)}
          {btn('=', equals, 'primary')}
        </Stack>
        <Button fullWidth variant="contained" disabled={!onSendToTender}
          onClick={() => onSendToTender?.(safeNumber(display, 0))}
          sx={{ mt: 0.5, bgcolor: ACCE_BRAND.orange, '&:hover': { bgcolor: ACCE_BRAND.orangeDark } }}>
          Use as Tendered
        </Button>
      </Box>
    </Paper>
  );
};

// ════════════════════════════════════════════
// BARCODE SCANNER DIALOG
// ════════════════════════════════════════════
const BarcodeScannerDialog = ({ open, onClose, onDetected }) => {
  const manualInputRef = useRef(null);
  const [tab, setTab] = useState(0);
  const [manualValue, setManualValue] = useState('');

  useEffect(() => {
    if (open) {
      setTab(0);
      setManualValue('');
      const t = setTimeout(() => manualInputRef.current?.focus(), 150);
      return () => clearTimeout(t);
    }
    setManualValue('');
    setTab(0);
    return undefined;
  }, [open]);

  const handleManualSubmit = (e) => {
    e.preventDefault();
    const v = manualValue.trim();
    if (!v) return;
    onDetected?.(v);
    setManualValue('');
    setTimeout(() => manualInputRef.current?.focus(), 100);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      TransitionComponent={Zoom}
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: alpha(ACCE_BRAND.navy, 0.05) }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <QrCodeScannerIcon sx={{ color: ACCE_BRAND.orange }} />
          <Typography variant="h6" fontWeight="bold">Scan Product</Typography>
        </Box>
        <IconButton size="small" onClick={onClose} aria-label="Close"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 3 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="fullWidth"
          sx={{
            mb: 2,
            '& .Mui-selected': { color: `${ACCE_BRAND.orange} !important` },
            '& .MuiTabs-indicator': { backgroundColor: ACCE_BRAND.orange },
          }}>
          <Tab icon={<KeyboardIcon />} iconPosition="start" label="Barcode Reader / Manual" />
          <Tab icon={<CameraAltIcon />} iconPosition="start" label="Camera" />
        </Tabs>
        {tab === 0 && (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }} icon={<FlashOnIcon />}>
              Point the scanner at the product. Or type the barcode and press Enter.
            </Alert>
            <Box component="form" onSubmit={handleManualSubmit}>
              <TextField inputRef={manualInputRef} autoFocus fullWidth label="Barcode / SKU"
                value={manualValue} onChange={(e) => setManualValue(e.target.value)}
                placeholder="e.g., 6151100010085"
                InputProps={{
                  startAdornment: (<InputAdornment position="start"><QrCodeScannerIcon /></InputAdornment>),
                  sx: { fontSize: '1.25rem', fontWeight: 600, letterSpacing: 1 },
                }} />
              <Button type="submit" variant="contained" fullWidth size="large"
                sx={{ mt: 2, bgcolor: ACCE_BRAND.orange, '&:hover': { bgcolor: ACCE_BRAND.orangeDark } }}
                disabled={!manualValue.trim()} startIcon={<SearchIcon />}>
                Add to Sale
              </Button>
            </Box>
          </Box>
        )}
        {tab === 1 && (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <CameraAltIcon sx={{ fontSize: 64, color: alpha(ACCE_BRAND.orange, 0.3), mb: 2 }} />
            <Typography variant="body1" color="text.secondary">Camera scanning disabled in preview.</Typography>
            <Typography variant="caption" color="text.secondary">Use the Barcode Reader tab to enter codes manually.</Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ════════════════════════════════════════════
// PRODUCT SEARCH DIALOG
// ════════════════════════════════════════════
const ProductSearchDialog = ({ open, onClose, onPick, products, loading, disabled }) => {
  const [q, setQ] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    setQ('');
    const t = setTimeout(() => inputRef.current?.focus(), 100);
    return () => clearTimeout(t);
  }, [open]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return products.slice(0, 50);
    return products.filter((p) =>
      (p.name || '').toLowerCase().includes(term) ||
      (p.barcode || '').toLowerCase().includes(term) ||
      (p.code || '').toLowerCase().includes(term) ||
      (p.category || '').toLowerCase().includes(term)
    ).slice(0, 100);
  }, [q, products]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 3, maxHeight: '80vh' } }}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" fontWeight="bold">Find Product</Typography>
        <IconButton size="small" onClick={onClose} aria-label="Close"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <TextField inputRef={inputRef} fullWidth autoFocus
          placeholder="Search name, barcode, SKU, category…"
          value={q} onChange={(e) => setQ(e.target.value)}
          InputProps={{
            startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>),
            endAdornment: q && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setQ('')} aria-label="Clear"><ClearIcon /></IconButton>
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }} />
        {loading && <LinearProgress />}
        <Stack spacing={1}>
          {filtered.length === 0 && !loading && (
            <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
              No products match.
            </Typography>
          )}
          {filtered.map((p) => {
            const qty = safeNumber(p.quantity, 0);
            const status = p.status || 'OUT_OF_STOCK';
            const qtyColor = status === 'IN_STOCK' ? '#4caf50' : status === 'LOW_STOCK' ? '#ff9800' : '#f44336';
            const pickable = qty > 0 && !disabled;
            return (
              <Paper key={p.id} onClick={() => pickable && onPick(p)}
                sx={{
                  p: 1.5, cursor: pickable ? 'pointer' : 'not-allowed',
                  borderRadius: 1.5, opacity: pickable ? 1 : 0.55,
                  display: 'flex', alignItems: 'center', gap: 1.5,
                  '&:hover': { bgcolor: pickable ? alpha(ACCE_BRAND.orange, 0.05) : 'transparent' },
                }}>
                <Avatar sx={{ bgcolor: alpha(ACCE_BRAND.orange, 0.1), color: ACCE_BRAND.orange }}>
                  <InventoryIcon fontSize="small" />
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle2" fontWeight={700} noWrap>{p.name}</Typography>
                  <Typography variant="caption" color="text.secondary" noWrap display="block">
                    {p.code || p.barcode || 'No SKU'}{p.category ? ` • ${p.category}` : ''}
                  </Typography>
                </Box>
                <Chip size="small" label={`Qty ${qty}`}
                  sx={{ bgcolor: alpha(qtyColor, 0.1), color: qtyColor, fontWeight: 700 }} />
                <Typography variant="body2" fontWeight={700} sx={{ minWidth: 90, textAlign: 'right' }}>
                  {formatCurrency(p.price)}
                </Typography>
              </Paper>
            );
          })}
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

// ════════════════════════════════════════════
// STUDENT WALLET GATE (verify-before-sell)
// ════════════════════════════════════════════
const StaffWalletGate = ({ onVerified }) => {
  const [staffId, setStaffId] = useState('');
  const [lookup, setLookup] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [avatarBroken, setAvatarBroken] = useState(false);
  const [imageZoomOpen, setImageZoomOpen] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => inputRef.current?.focus(), 150);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    setAvatarBroken(false);
    setImageZoomOpen(false);
  }, [lookup?.user?.staff_id]);

  // Debounced lookup as the user types
  useEffect(() => {
    const id = staffId.trim();
    if (!id) {
      setLookup(null);
      setError('');
      return undefined;
    }
    const handle = setTimeout(async () => {
      setLoading(true);
      setError('');
      const res = await apiLookupWallet(id);
      setLoading(false);
      if (!res.success) {
        setLookup(null);
        setError(res.notFound ? `No student with ID "${id}".` : (res.error || 'Lookup failed.'));
        return;
      }
      setLookup({ user: res.user, wallet: res.wallet });
    }, 450);
    return () => clearTimeout(handle);
  }, [staffId]);

  const balance = safeNumber(lookup?.wallet?.balance, 0);
  const isLocked = !!lookup?.wallet?.is_locked;
  const zeroBalance = balance <= 0;
  const canStart = !!lookup && !isLocked && !zeroBalance;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!canStart) return;
    onVerified({
      user: lookup.user,
      wallet: lookup.wallet,
      staff_id: lookup.user?.staff_id || staffId.trim(),
      starting_balance: balance,
    });
  };

  const rawAvatar = lookup?.user?.avatar;
  const avatarSrc =
    !avatarBroken && typeof rawAvatar === 'string' && rawAvatar.trim().length > 0
      ? rawAvatar
      : undefined;

  const initials = getInitials(lookup?.user?.full_name);
  const hasPhoto = !!avatarSrc;

  return (
    <Box sx={{
      minHeight: 'calc(100vh - 200px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      py: 4,
    }}>
      <Paper elevation={6} sx={{
        p: { xs: 3, sm: 4 }, borderRadius: 3, width: '100%', maxWidth: 720,
        border: `1px solid ${alpha(ACCE_BRAND.orange, 0.25)}`,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
          <Avatar sx={{ bgcolor: alpha(ACCE_BRAND.orange, 0.15), color: ACCE_BRAND.orange }}>
            <WalletIcon />
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight={800}>Verify Student Wallet</Typography>
            <Typography variant="caption" color="text.secondary">
              Enter the Student ID of the wallet holder to start a sale.
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            inputRef={inputRef}
            fullWidth
            required
            autoFocus
            label="Payer Student ID"
            value={staffId}
            onChange={(e) => setStaffId(e.target.value)}
            placeholder="e.g., ADM001"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonSearchIcon sx={{ color: alpha(ACCE_BRAND.orange, 0.8) }} />
                </InputAdornment>
              ),
              endAdornment: loading && (
                <InputAdornment position="end">
                  <CircularProgress size={18} />
                </InputAdornment>
              ),
            }}
          />

          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

          {lookup && (
            <Box sx={{ mt: 2 }}>
              <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2.5, bgcolor: alpha(ACCE_BRAND.navy, 0.02) }}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={3}
                  alignItems={{ xs: 'center', sm: 'flex-start' }}
                >
                  {/* ── Large photo (click to zoom) ── */}
                  <Tooltip title={hasPhoto ? 'Click to enlarge' : 'No photo on file'}>
                    <Box
                      role={hasPhoto ? 'button' : undefined}
                      tabIndex={hasPhoto ? 0 : -1}
                      onClick={() => hasPhoto && setImageZoomOpen(true)}
                      onKeyDown={(e) => {
                        if (!hasPhoto) return;
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setImageZoomOpen(true);
                        }
                      }}
                      sx={{
                        position: 'relative',
                        borderRadius: '50%',
                        flexShrink: 0,
                        cursor: hasPhoto ? 'zoom-in' : 'default',
                        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                        outline: 'none',
                        '&:hover': hasPhoto ? {
                          transform: 'scale(1.03)',
                          boxShadow: `0 12px 32px ${alpha(ACCE_BRAND.orange, 0.35)}`,
                        } : undefined,
                        '&:focus-visible': hasPhoto ? {
                          boxShadow: `0 0 0 4px ${alpha(ACCE_BRAND.orange, 0.35)}`,
                        } : undefined,
                      }}
                    >
                      <Avatar
                        src={avatarSrc}
                        alt={lookup.user.full_name || 'Student'}
                        onError={() => setAvatarBroken(true)}
                        imgProps={{ referrerPolicy: 'no-referrer' }}
                        sx={{
                          bgcolor: alpha(ACCE_BRAND.orange, 0.15),
                          color: ACCE_BRAND.orange,
                          fontWeight: 800,
                          fontSize: { xs: '2.5rem', sm: '3rem' },
                          width: { xs: 110, sm: 128 },
                          height: { xs: 110, sm: 128 },
                          border: `4px solid ${alpha(ACCE_BRAND.orange, 0.35)}`,
                          boxShadow: `0 8px 24px ${alpha(ACCE_BRAND.orange, 0.18)}`,
                          flexShrink: 0,
                        }}
                      >
                        {initials}
                      </Avatar>

                      {hasPhoto && (
                        <Box
                          sx={{
                            position: 'absolute',
                            right: -4,
                            bottom: -4,
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            bgcolor: ACCE_BRAND.orange,
                            color: '#fff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: '2px solid #fff',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                            pointerEvents: 'none',
                          }}
                        >
                          <ZoomInIcon sx={{ fontSize: 18 }} />
                        </Box>
                      )}
                    </Box>
                  </Tooltip>

                  {/* ── User details ── */}
                  <Box sx={{
                    flex: 1,
                    minWidth: 0,
                    width: '100%',
                    textAlign: { xs: 'center', sm: 'left' },
                  }}>
                    <Typography
                      variant="h5"
                      fontWeight={800}
                      sx={{ wordBreak: 'break-word', lineHeight: 1.2 }}
                    >
                      {lookup.user.full_name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {lookup.user.staff_id}
                      {lookup.user.role ? ` · ${lookup.user.role}` : ''}
                      {lookup.user.department ? ` · ${lookup.user.department}` : ''}
                    </Typography>
                    {lookup.user.email && (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                        {lookup.user.email}
                      </Typography>
                    )}
                    {lookup.user.phone && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {lookup.user.phone}
                      </Typography>
                    )}
                  </Box>

                  {/* ── Balance ── */}
                  <Box sx={{
                    textAlign: { xs: 'center', sm: 'right' },
                    flexShrink: 0,
                    minWidth: { xs: '100%', sm: 140 },
                  }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Wallet Balance
                    </Typography>
                    <Typography
                      variant="h4"
                      fontWeight={800}
                      sx={{ color: ACCE_BRAND.orange, lineHeight: 1.1 }}
                    >
                      {formatCurrency(balance)}
                    </Typography>
                  </Box>
                </Stack>

                <Divider sx={{ my: 2 }} />

                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" justifyContent={{ xs: 'center', sm: 'flex-start' }}>
                  <Chip
                    size="small"
                    icon={isLocked ? <LockIcon /> : <LockOpenIcon />}
                    label={isLocked ? 'Wallet Locked' : 'Wallet Active'}
                    color={isLocked ? 'error' : 'success'}
                    variant="outlined"
                  />
                  {zeroBalance && !isLocked && (
                    <Chip size="small" icon={<WarningIcon />} label="Zero Balance" color="warning" variant="outlined" />
                  )}
                </Stack>

                {isLocked && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    <strong>Wallet is locked.</strong> This student cannot make purchases until the wallet is unlocked.
                  </Alert>
                )}

                {!isLocked && zeroBalance && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    <strong>Zero balance.</strong> Please top up the wallet before making a sale.
                  </Alert>
                )}

                {canStart && (
                  <Alert severity="success" sx={{ mt: 2 }} icon={<CheckCircleIcon />}>
                    Wallet verified — ready to start a sale.
                  </Alert>
                )}
              </Paper>
            </Box>
          )}

          <Button
            type="submit"
            fullWidth
            size="large"
            variant="contained"
            disabled={!canStart}
            startIcon={<CheckCircleIcon />}
            sx={{
              mt: 3,
              ...glossyButton(ACCE_BRAND.orange, ACCE_BRAND.orangeDark, ACCE_BRAND.orangeLight),
              fontWeight: 800,
              py: 1.4,
            }}
          >
            Start Sale
          </Button>
        </Box>
      </Paper>

      {/* ════════════════════════════════════════════
          IMAGE ZOOM LIGHTBOX
          ════════════════════════════════════════════ */}
      <Dialog
        open={imageZoomOpen}
        onClose={() => setImageZoomOpen(false)}
        maxWidth="lg"
        fullWidth
        TransitionComponent={Zoom}
        PaperProps={{
          sx: {
            bgcolor: 'rgba(15, 23, 42, 0.92)',
            backgroundImage: 'none',
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: '0 24px 80px rgba(0,0,0,0.6)',
          },
        }}
        slotProps={{
          backdrop: {
            sx: { bgcolor: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)' },
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            color: '#fff',
            borderBottom: '1px solid rgba(255,255,255,0.1)',
            py: 1.5,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
            <ZoomOutMapIcon sx={{ color: ACCE_BRAND.orange }} />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" fontWeight={800} noWrap sx={{ color: '#fff' }}>
                {lookup?.user?.full_name || 'Student Photo'}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }} noWrap display="block">
                {lookup?.user?.staff_id || ''}
                {lookup?.user?.role ? ` · ${lookup.user.role}` : ''}
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={() => setImageZoomOpen(false)}
            aria-label="Close zoomed image"
            sx={{
              color: '#fff',
              bgcolor: 'rgba(255,255,255,0.08)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.18)' },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          onClick={() => setImageZoomOpen(false)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: { xs: 2, sm: 4 },
            cursor: 'zoom-out',
            minHeight: { xs: '60vh', sm: '70vh' },
          }}
        >
          <Avatar
            src={avatarSrc}
            alt={lookup?.user?.full_name || 'Student'}
            imgProps={{
              referrerPolicy: 'no-referrer',
              style: {
                objectFit: 'cover',
                imageRendering: 'auto',
              },
            }}
            sx={{
              width: { xs: '80vw', sm: '60vw', md: '50vw', lg: '42vw' },
              height: { xs: '80vw', sm: '60vw', md: '50vw', lg: '42vw' },
              maxWidth: 640,
              maxHeight: 640,
              bgcolor: alpha(ACCE_BRAND.orange, 0.2),
              color: ACCE_BRAND.orange,
              fontSize: { xs: '4rem', sm: '6rem' },
              fontWeight: 800,
              border: `6px solid ${alpha(ACCE_BRAND.orange, 0.55)}`,
              boxShadow: `0 24px 80px ${alpha(ACCE_BRAND.orange, 0.35)}`,
              cursor: 'default',
            }}
          >
            {initials}
          </Avatar>
        </DialogContent>

        <DialogActions
          sx={{
            justifyContent: 'space-between',
            px: 2.5,
            py: 1.5,
            borderTop: '1px solid rgba(255,255,255,0.1)',
          }}
        >
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
            Click anywhere on the image to close
          </Typography>
          <Button
            onClick={() => setImageZoomOpen(false)}
            variant="contained"
            startIcon={<CloseIcon />}
            sx={{
              ...glossyButton(ACCE_BRAND.orange, ACCE_BRAND.orangeDark, ACCE_BRAND.orangeLight),
              textTransform: 'none',
              fontWeight: 800,
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ════════════════════════════════════════════
// WALLET BANNER (shown above the terminal once verified)
// ════════════════════════════════════════════
const WalletBanner = ({ walletInfo, runningTotal, onChangePayer }) => {
  const balance = safeNumber(walletInfo?.wallet?.balance, 0);
  const remaining = round2(balance - runningTotal);
  const over = remaining < 0;
  const [avatarBroken, setAvatarBroken] = useState(false);

  useEffect(() => { setAvatarBroken(false); }, [walletInfo?.user?.staff_id]);

  const rawAvatar = walletInfo?.user?.avatar;
  const avatarSrc =
    !avatarBroken && typeof rawAvatar === 'string' && rawAvatar.trim().length > 0
      ? rawAvatar
      : undefined;
  const initials = getInitials(walletInfo?.user?.full_name);

  return (
    <Paper elevation={2} sx={{
      p: 1.5, borderRadius: 2,
      bgcolor: alpha(ACCE_BRAND.orange, 0.05),
      border: `1px solid ${alpha(ACCE_BRAND.orange, 0.3)}`,
      display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap',
    }}>
      <Avatar
        src={avatarSrc}
        alt={walletInfo?.user?.full_name || ''}
        onError={() => setAvatarBroken(true)}
        imgProps={{ referrerPolicy: 'no-referrer' }}
        sx={{
          bgcolor: alpha(ACCE_BRAND.orange, 0.15),
          color: ACCE_BRAND.orange,
          width: 56, height: 56,
          fontWeight: 800,
          fontSize: '1.1rem',
          border: `2px solid ${alpha(ACCE_BRAND.orange, 0.3)}`,
        }}
      >
        {initials}
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 180 }}>
        <Typography variant="body2" fontWeight={800} noWrap>
          {walletInfo?.user?.full_name}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap display="block">
          {walletInfo?.user?.staff_id} · {walletInfo?.user?.role || 'student'}
        </Typography>
      </Box>

      <Box sx={{ textAlign: 'right' }}>
        <Typography variant="caption" color="text.secondary" display="block">Wallet Balance</Typography>
        <Typography variant="body2" fontWeight={800} sx={{ color: ACCE_BRAND.orange }}>
          {formatCurrency(balance)}
        </Typography>
      </Box>

      <Box sx={{ textAlign: 'right' }}>
        <Typography variant="caption" color="text.secondary" display="block">Cart Total</Typography>
        <Typography variant="body2" fontWeight={800}>
          {formatCurrency(runningTotal)}
        </Typography>
      </Box>

      <Box sx={{ textAlign: 'right' }}>
        <Typography variant="caption" color="text.secondary" display="block">Remaining</Typography>
        <Typography variant="body2" fontWeight={800} sx={{ color: over ? '#c62828' : '#2e7d32' }}>
          {formatCurrency(remaining)}
        </Typography>
      </Box>

      <Tooltip title="End this wallet session">
        <Button
          size="small"
          variant="outlined"
          startIcon={<LogoutIcon />}
          onClick={onChangePayer}
          sx={{ textTransform: 'none', borderColor: ACCE_BRAND.navy, color: ACCE_BRAND.navy }}
        >
          Change Payer
        </Button>
      </Tooltip>
    </Paper>
  );
};

// ════════════════════════════════════════════
// CHARGE (CONFIRM) DIALOG — wallet only
// ════════════════════════════════════════════
const ChargeDialog = ({ open, onClose, cart, walletInfo, onConfirm, submitting }) => {
  const totals = useMemo(() => computeTotals(cart), [cart]);
  const balance = safeNumber(walletInfo?.wallet?.balance, 0);
  const balanceAfter = round2(balance - totals.total);
  const sufficient = balanceAfter >= 0;
  const [avatarBroken, setAvatarBroken] = useState(false);

  useEffect(() => { setAvatarBroken(false); }, [walletInfo?.user?.staff_id, open]);

  const rawAvatar = walletInfo?.user?.avatar;
  const avatarSrc =
    !avatarBroken && typeof rawAvatar === 'string' && rawAvatar.trim().length > 0
      ? rawAvatar
      : undefined;
  const initials = getInitials(walletInfo?.user?.full_name);

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WalletIcon sx={{ color: ACCE_BRAND.orange }} />
          <Typography variant="h6" fontWeight="bold">Confirm Wallet Charge</Typography>
        </Box>
        <IconButton size="small" onClick={onClose} disabled={submitting} aria-label="Close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: alpha(ACCE_BRAND.navy, 0.04) }}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">Subtotal</Typography>
              <Typography variant="body1" fontWeight={700}>{formatCurrency(totals.subtotal)}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">Discount</Typography>
              <Typography variant="body1" fontWeight={700}>− {formatCurrency(totals.discountTotal)}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">Tax</Typography>
              <Typography variant="body1" fontWeight={700}>{formatCurrency(totals.taxTotal)}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">Total</Typography>
              <Typography variant="h5" fontWeight={800} color={ACCE_BRAND.orange}>
                {formatCurrency(totals.total)}
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar
              src={avatarSrc}
              alt={walletInfo?.user?.full_name || ''}
              onError={() => setAvatarBroken(true)}
              imgProps={{ referrerPolicy: 'no-referrer' }}
              sx={{
                bgcolor: alpha(ACCE_BRAND.orange, 0.15),
                color: ACCE_BRAND.orange,
                fontWeight: 800,
                width: 56, height: 56,
                border: `2px solid ${alpha(ACCE_BRAND.orange, 0.3)}`,
              }}
            >
              {initials}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" fontWeight={800} noWrap>
                {walletInfo?.user?.full_name}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap display="block">
                {walletInfo?.user?.staff_id} · {walletInfo?.user?.role || 'student'}
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="caption" color="text.secondary" display="block">Balance</Typography>
              <Typography variant="body2" fontWeight={800} sx={{ color: ACCE_BRAND.orange }}>
                {formatCurrency(balance)}
              </Typography>
            </Box>
          </Stack>

          <Divider sx={{ my: 1.5 }} />

          <Grid container spacing={1}>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary" display="block">Amount to charge</Typography>
              <Typography variant="body2" fontWeight={700}>{formatCurrency(totals.total)}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary" display="block">Balance after</Typography>
              <Typography variant="body2" fontWeight={700} sx={{ color: sufficient ? '#2e7d32' : '#c62828' }}>
                {formatCurrency(Math.max(0, balanceAfter))}
              </Typography>
            </Grid>
          </Grid>

          {!sufficient && (
            <Alert severity="error" sx={{ mt: 2 }}>
              <strong>Insufficient funds.</strong> Short by {formatCurrency(Math.abs(balanceAfter))}.
            </Alert>
          )}

          {sufficient && (
            <Alert severity="success" sx={{ mt: 2 }} icon={<CheckCircleIcon />}>
              Wallet has sufficient funds for this purchase.
            </Alert>
          )}
        </Paper>
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={onClose} disabled={submitting} color="inherit">Cancel</Button>
        <Button
          onClick={() => sufficient && onConfirm()}
          variant="contained"
          disabled={!sufficient || submitting}
          startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <CheckCircleIcon />}
          sx={{ ...glossyButton(ACCE_BRAND.orange, ACCE_BRAND.orangeDark, ACCE_BRAND.orangeLight) }}
        >
          {submitting ? 'Processing…' : `Charge Wallet ${formatCurrency(totals.total)}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ════════════════════════════════════════════
// RECEIPT DIALOG
// ════════════════════════════════════════════
const RECEIPT_CSS = `
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; background: #fff; color: #000; font-family: 'Courier New', 'Courier', monospace; font-size: 11px; line-height: 1.28; }
  .r-wrap { width: 300px; margin: 0 auto; padding: 4px 6px 6px 6px; }
  .r-center { text-align: center; }
  .r-bold { font-weight: 700; }
  .r-brand { font-size: 15px; font-weight: 800; letter-spacing: 0.6px; }
  .r-muted { color: #222; }
  .r-sep { border: 0; border-top: 1px dashed #000; margin: 4px 0; }
  .r-row { display: flex; justify-content: space-between; gap: 6px; }
  .r-sku { font-size: 9.5px; }
  .r-grand { display: flex; justify-content: space-between; font-size: 14px; font-weight: 800; margin-top: 2px; }
  table.r-table { width: 100%; border-collapse: collapse; }
  table.r-table th, table.r-table td { padding: 1px 0; vertical-align: top; font-size: 10.5px; }
  table.r-table th { border-bottom: 1px dashed #000; font-weight: 700; }
  .r-th-qty { text-align: left; width: 55%; }
  .r-th-disc { text-align: right; width: 18%; }
  .r-th-tot { text-align: right; width: 27%; }
  @media print { @page { size: 80mm auto; margin: 0; } html, body { width: 80mm; } .r-wrap { width: 80mm; padding: 2mm 3mm 3mm 3mm; } }
`;

const buildReceiptHtml = (sale, locationName) => {
  const safe = (v) => (v == null ? '' : String(v));
  const esc = (v) => safe(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const fmt = (v) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return '0.00';
    return n.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };
  const itemsHtml = (sale.items || []).map((it) => {
    const sku = it.product_code || it.product_barcode || '';
    const discount = Number(it.line_discount) || 0;
    return `
      <tr><td class="r-th-qty" colspan="3">
        <div class="r-bold">${esc(it.product_name)}</div>
        ${sku ? `<div class="r-sku r-muted">SKU: ${esc(sku)}</div>` : ''}
      </td></tr>
      <tr>
        <td class="r-th-qty">${esc(it.quantity)} × ${esc(fmt(it.unit_price))}</td>
        <td class="r-th-disc">${discount > 0 ? '-' + esc(fmt(discount)) : ''}</td>
        <td class="r-th-tot r-bold">${esc(fmt(it.line_total))}</td>
      </tr>
    `;
  }).join('');
  const orgAddressLine = [ORG.address, ORG.city, ORG.state].filter(Boolean).join(', ');

  return `<!DOCTYPE html><html><head><meta charset="utf-8" /><title>Receipt ${esc(sale.reference)}</title><style>${RECEIPT_CSS}</style></head><body>
    <div class="r-wrap">
      <div class="r-center r-brand">${esc(ORG.name)}</div>
      ${orgAddressLine ? `<div class="r-center r-muted">${esc(orgAddressLine)}</div>` : ''}
      ${ORG.phone ? `<div class="r-center r-muted">Tel: ${esc(ORG.phone)}</div>` : ''}
      <hr class="r-sep" />
      <div class="r-row"><span class="r-bold">STORE</span><span>${esc(locationName)}</span></div>
      <div class="r-row"><span class="r-bold">RECEIPT</span><span>${esc(sale.reference)}</span></div>
      <div class="r-row"><span class="r-bold">DATE</span><span>${esc(new Date(sale.sold_at || sale.created_at).toLocaleString('en-NG'))}</span></div>
      ${sale.cashier_name ? `<div class="r-row"><span class="r-bold">CASHIER</span><span>${esc(sale.cashier_name)}</span></div>` : ''}
      <hr class="r-sep" />
      <div class="r-row"><span class="r-bold">PAID BY</span><span>${esc(sale.payer_name || '')}</span></div>
      <div class="r-row"><span>STUDENT ID</span><span>${esc(sale.payer_staff_id || '')}</span></div>
      ${sale.wallet_reference ? `<div class="r-row"><span>WALLET REF</span><span>${esc(sale.wallet_reference)}</span></div>` : ''}
      ${typeof sale.wallet_balance_after === 'number' ? `<div class="r-row"><span>NEW BALANCE</span><span>₦${esc(fmt(sale.wallet_balance_after))}</span></div>` : ''}
      <hr class="r-sep" />
      <table class="r-table"><thead><tr><th class="r-th-qty">Item</th><th class="r-th-disc">Disc</th><th class="r-th-tot">Amount</th></tr></thead><tbody>${itemsHtml}</tbody></table>
      <hr class="r-sep" />
      <div class="r-row"><span>Subtotal</span><span>${esc(fmt(sale.subtotal))}</span></div>
      <div class="r-row"><span>Discount</span><span>-${esc(fmt(sale.discount_total))}</span></div>
      <div class="r-row"><span>Tax</span><span>${esc(fmt(sale.tax_total))}</span></div>
      <hr class="r-sep" />
      <div class="r-grand"><span>TOTAL</span><span>₦${esc(fmt(sale.total))}</span></div>
      <hr class="r-sep" />
      <div class="r-center r-bold">Thank you for your patronage!</div>
      <div class="r-center r-muted r-sku">*** END OF RECEIPT ***</div>
    </div></body></html>`;
};

const ReceiptDialog = ({ open, sale, locationName, onClose, onNewSale, onExitSession }) => {
  if (!sale) return null;
  const handlePrint = () => {
    const html = buildReceiptHtml(sale, locationName);
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed'; iframe.style.right = '0'; iframe.style.bottom = '0';
    iframe.style.width = '0'; iframe.style.height = '0'; iframe.style.border = '0';
    document.body.appendChild(iframe);
    const doc = iframe.contentWindow?.document;
    if (!doc) { document.body.removeChild(iframe); return; }
    doc.open(); doc.write(html); doc.close();
    iframe.onload = () => {
      try { iframe.contentWindow.focus(); iframe.contentWindow.print(); }
      finally { setTimeout(() => { if (iframe.parentNode) iframe.parentNode.removeChild(iframe); }, 800); }
    };
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 3, maxHeight: '90vh' } }}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: alpha('#4caf50', 0.08) }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckCircleIcon sx={{ color: '#2e7d32' }} />
          <Typography variant="h6" fontWeight="bold">Sale Completed</Typography>
        </Box>
        <IconButton size="small" onClick={onClose} aria-label="Close"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <Typography variant="h6" fontWeight={800}>{locationName}</Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            {sale.reference} • {new Date(sale.sold_at || sale.created_at).toLocaleString('en-NG')}
          </Typography>
        </Box>
        <Divider sx={{ mb: 2 }} />
        <Stack spacing={1} sx={{ mb: 2 }}>
          {(sale.items || []).map((it) => (
            <Box key={it.id} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={600} noWrap>{it.product_name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {it.quantity} × {formatCurrency(it.unit_price)}
                </Typography>
              </Box>
              <Typography variant="body2" fontWeight={700}>{formatCurrency(it.line_total)}</Typography>
            </Box>
          ))}
        </Stack>
        <Divider sx={{ mb: 2 }} />
        <Stack spacing={0.5}>
          <Row label="Subtotal" value={formatCurrency(sale.subtotal)} />
          <Row label="Discount" value={`− ${formatCurrency(sale.discount_total)}`} />
          <Row label="Tax" value={formatCurrency(sale.tax_total)} />
          <Divider sx={{ my: 1 }} />
          <Row label="Total" value={formatCurrency(sale.total)} bold accent />
          <Row label="Paid by" value={`${sale.payer_name || ''} (${sale.payer_staff_id || ''})`} />
          {sale.wallet_reference && <Row label="Wallet ref" value={sale.wallet_reference} />}
          {typeof sale.wallet_balance_after === 'number' && (
            <Row label="Wallet balance" value={formatCurrency(sale.wallet_balance_after)} />
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1, flexWrap: 'wrap' }}>
        <Button onClick={handlePrint} startIcon={<PrintIcon />} color="inherit">Print Receipt</Button>
        <Button
          onClick={onExitSession}
          variant="outlined"
          color="inherit"
          startIcon={<ExitToAppIcon />}
          sx={{ textTransform: 'none' }}
        >
          Exit
        </Button>
        <Button onClick={onNewSale} variant="contained"
          sx={{ ...glossyButton(ACCE_BRAND.orange, ACCE_BRAND.orangeDark, ACCE_BRAND.orangeLight) }}>
          New Sale
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ════════════════════════════════════════════
// ORDER TRAY
// ════════════════════════════════════════════
const OrderTray = ({
  cart, onQtyChange, onRemove, onLineDiscount,
  onClear, onHold, heldCount, onResume, onCharge, onOpenSearch,
  onOpenCalculator, submitting, walletInfo, onChangePayer,
}) => {
  const totals = useMemo(() => computeTotals(cart), [cart]);
  const balance = safeNumber(walletInfo?.wallet?.balance, 0);
  const remaining = round2(balance - totals.total);
  const overBudget = remaining < 0;

  return (
    <Paper elevation={3} sx={{ height: '100%', display: 'flex', flexDirection: 'column', borderRadius: 2, overflow: 'hidden' }}>
      <Box sx={{ p: 2, bgcolor: alpha(ACCE_BRAND.navy, 0.06), borderBottom: `1px solid ${alpha(ACCE_BRAND.navy, 0.2)}` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ReceiptIcon sx={{ color: ACCE_BRAND.navy }} />
            <Typography variant="subtitle1" fontWeight={800}>Current Sale</Typography>
          </Box>
          <Chip size="small" label={`${cart.length} item${cart.length === 1 ? '' : 's'}`}
            sx={{ bgcolor: alpha(ACCE_BRAND.orange, 0.15), color: ACCE_BRAND.orange, fontWeight: 700 }} />
        </Box>
        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
          <Button size="small" startIcon={<SearchIcon />} onClick={onOpenSearch} variant="outlined"
            sx={{ borderColor: ACCE_BRAND.navy, color: ACCE_BRAND.navy, flex: 1, textTransform: 'none' }}>
            Find (F2)
          </Button>
          <Tooltip title="Hold current sale">
            <Button size="small" variant="outlined" onClick={onHold} disabled={cart.length === 0}
              startIcon={<HoldIcon />} sx={{ minWidth: 'auto', px: 1.2 }}>Hold</Button>
          </Tooltip>
          <Tooltip title={`Resume held sale (${heldCount})`}>
            <span>
              <Button size="small" variant="outlined" onClick={onResume} disabled={heldCount === 0}
                startIcon={<ResumeIcon />} sx={{ minWidth: 'auto', px: 1.2 }}>{heldCount > 0 ? heldCount : ''}</Button>
            </span>
          </Tooltip>
          <Tooltip title="Clear sale">
            <span>
              <IconButton size="small" onClick={onClear} disabled={cart.length === 0} aria-label="Clear sale">
                <DeleteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Box>
      <Box sx={{ flex: 1, overflowY: 'auto', p: 1.5 }}>
        {cart.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <InventoryIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 1.5 }} />
            <Typography variant="subtitle1" color="text.secondary" fontWeight={600}>Ready to scan</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Scan a barcode or press F2 to search.
            </Typography>
          </Box>
        ) : (
          <Stack spacing={1}>
            {cart.map((line, idx) => (
              <Paper key={line.key} variant="outlined" sx={{ p: 1.2, borderRadius: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={700} noWrap title={line.product_name}>
                      {idx + 1}. {line.product_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" noWrap>
                      {line.product_code || line.product_barcode || 'No SKU'}
                    </Typography>
                  </Box>
                  <IconButton size="small" onClick={() => onRemove(line.key)} aria-label="Remove line">
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <IconButton size="small" onClick={() => onQtyChange(line.key, line.quantity - 1)}
                      sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }} aria-label="Decrease">
                      <RemoveCircleOutlineIcon fontSize="small" />
                    </IconButton>
                    <TextField size="small" value={line.quantity}
                      onChange={(e) => {
                        const v = parseInt(e.target.value, 10);
                        onQtyChange(line.key, Number.isFinite(v) ? v : 0);
                      }}
                      inputProps={{ min: 1, style: { textAlign: 'center', width: 40, padding: '4px 0' } }}
                      sx={{ width: 60 }} />
                    <IconButton size="small" onClick={() => onQtyChange(line.key, line.quantity + 1)}
                      sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }} aria-label="Increase">
                      <AddCircleOutlineIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                  <Typography variant="body2" fontWeight={800}>
                    {formatCurrency(round2(line.unit_price * line.quantity - (line.line_discount || 0)))}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                  <Typography variant="caption" color="text.secondary">Discount:</Typography>
                  <TextField size="small" type="number" value={line.line_discount ?? 0}
                    onChange={(e) => onLineDiscount(line.key, e.target.value)}
                    inputProps={{ min: 0, step: 0.01 }} sx={{ width: 110 }}
                    InputProps={{ startAdornment: (<InputAdornment position="start"><LocalOfferIcon sx={{ fontSize: 14 }} /></InputAdornment>) }} />
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                    {formatCurrency(line.unit_price)} ea
                  </Typography>
                </Box>
              </Paper>
            ))}
          </Stack>
        )}
      </Box>
      <Box sx={{ p: 2, borderTop: `1px solid ${alpha(ACCE_BRAND.navy, 0.2)}`, bgcolor: alpha(ACCE_BRAND.navy, 0.03) }}>
        <Stack spacing={0.5} sx={{ mb: 1.5 }}>
          <Row label="Subtotal" value={formatCurrency(totals.subtotal)} />
          <Row label="Discount" value={`− ${formatCurrency(totals.discountTotal)}`} />
          <Row label="Tax" value={formatCurrency(totals.taxTotal)} />
          <Divider sx={{ my: 0.5 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <Typography variant="body2" fontWeight={700}>Total</Typography>
            <Typography variant="h5" fontWeight={800} color={ACCE_BRAND.orange}>{formatCurrency(totals.total)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption" color="text.secondary">Wallet remaining</Typography>
            <Typography variant="caption" fontWeight={800} sx={{ color: overBudget ? '#c62828' : '#2e7d32' }}>
              {formatCurrency(remaining)}
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Open calculator">
            <IconButton onClick={onOpenCalculator} sx={{ border: '1px solid', borderColor: 'divider' }} aria-label="Calculator">
              <CalculateIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Change wallet payer">
            <IconButton onClick={onChangePayer} sx={{ border: '1px solid', borderColor: 'divider' }} aria-label="Change payer">
              <LogoutIcon />
            </IconButton>
          </Tooltip>
          <Button fullWidth variant="contained" size="large"
            disabled={cart.length === 0 || submitting || overBudget} onClick={onCharge}
            startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : "₦"}
            sx={{ ...glossyButton(ACCE_BRAND.orange, ACCE_BRAND.orangeDark, ACCE_BRAND.orangeLight), fontWeight: 800, py: 1.4 }}>
            Charge (F9)
          </Button>
        </Stack>
      </Box>
    </Paper>
  );
};

// ════════════════════════════════════════════
// MAIN SALES TERMINAL
// ════════════════════════════════════════════
const SalesTerminal = () => {
  const [activeMenu, setActiveMenu] = useState('Menu');

  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [todayStats, setTodayStats] = useState(null);

  const [walletInfo, setWalletInfo] = useState(() => loadSession());
  const [cart, setCart] = useState(() => loadCart());
  const [heldSales, setHeldSales] = useState([]);

  const [barcodeValue, setBarcodeValue] = useState('');
  const [scanFeedback, setScanFeedback] = useState(null);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [calcOpen, setCalcOpen] = useState(false);
  const [chargeOpen, setChargeOpen] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const barcodeInputRef = useRef(null);
  const isMountedRef = useRef(true);
  const lastAddRef = useRef({ barcode: '', ts: 0 });

  const walletBalance = safeNumber(walletInfo?.wallet?.balance, 0);
  const walletLocked = !!walletInfo?.wallet?.is_locked;
  const cartTotal = useMemo(() => computeTotals(cart).total, [cart]);
  const overBudget = cartTotal > walletBalance;

  useEffect(() => { saveSession(walletInfo); }, [walletInfo]);
  useEffect(() => { saveCart(cart); }, [cart]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  const pushSnackbar = useCallback((message, severity = 'success') => {
    if (!isMountedRef.current) return;
    setSnackbar({ open: true, message, severity });
  }, []);

  const closeSnackbar = useCallback(() => setSnackbar((s) => ({ ...s, open: false })), []);

  const safeFocusBarcode = useCallback((delay = 50) => {
    setTimeout(() => {
      if (!isMountedRef.current) return;
      barcodeInputRef.current?.focus();
    }, delay);
  }, []);

  const fetchProducts = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setProductsLoading(true);
    const res = await apiListProducts({ page: 1, page_size: 200 });
    if (!isMountedRef.current) return;
    if (res.success) {
      setProducts(res.data || []);
    } else {
      pushSnackbar(res.error || 'Failed to load products', 'error');
      setProducts([]);
    }
    if (!silent) setProductsLoading(false);
  }, [pushSnackbar]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const fetchTodayStats = useCallback(async () => {
    const res = await apiTodayStats();
    if (!isMountedRef.current) return;
    if (res.success) setTodayStats(res.stats);
  }, []);

  useEffect(() => { fetchTodayStats(); }, [fetchTodayStats]);

  const startSession = useCallback((info) => {
    setWalletInfo(info);
    setCart([]);
    setHeldSales([]);
    setScanFeedback(null);
    pushSnackbar(`Wallet verified — ${info.user?.full_name} (${info.user?.staff_id}).`, 'success');
    safeFocusBarcode(300);
  }, [pushSnackbar, safeFocusBarcode]);

  const endSession = useCallback(() => {
    if (cart.length > 0) {
      const ok = window.confirm('There are items in the current cart. Ending the session will discard them. Continue?');
      if (!ok) return;
    }
    setWalletInfo(null);
    setCart([]);
    setHeldSales([]);
    setScanFeedback(null);
    setBarcodeValue('');
    setReceipt(null);
    pushSnackbar('Wallet session ended.', 'info');
  }, [cart.length, pushSnackbar]);

  const refreshWallet = useCallback(async () => {
    if (!walletInfo?.user?.staff_id) return;
    const res = await apiLookupWallet(walletInfo.user.staff_id);
    if (!isMountedRef.current) return;
    if (res.success) {
      setWalletInfo((prev) => prev ? { ...prev, user: res.user, wallet: res.wallet } : prev);
      if (res.wallet?.is_locked) {
        pushSnackbar('Wallet is now locked. Please contact an administrator.', 'warning');
      }
    }
  }, [walletInfo, pushSnackbar]);

  const addProductToCart = useCallback((product, qty = 1) => {
    if (!walletInfo) {
      pushSnackbar('Verify a student wallet before adding items.', 'warning');
      return;
    }
    if (walletLocked) {
      pushSnackbar('Wallet is locked — cannot add items.', 'error');
      return;
    }

    setCart((prev) => {
      const existing = prev.find((l) => l.product_id === product.id);
      const avail = safeNumber(product.quantity, 0);
      const unitPrice = safeNumber(product.price, 0);

      const currentTotal = computeTotals(prev).total;

      let nextQty;
      let deltaQty;

      if (existing) {
        nextQty = existing.quantity + qty;
        deltaQty = qty;
        if (nextQty > avail) {
          setScanFeedback({ type: 'warning', message: `Only ${avail} of "${product.name}" in stock.` });
          pushSnackbar(`Cannot add more — only ${avail} in stock.`, 'warning');
          return prev;
        }
      } else {
        nextQty = qty;
        deltaQty = qty;
        if (avail < qty) {
          setScanFeedback({ type: 'error', message: `"${product.name}" is out of stock.` });
          pushSnackbar(`"${product.name}" is out of stock.`, 'error');
          return prev;
        }
      }

      const projectedTotal = round2(currentTotal + unitPrice * deltaQty);
      if (projectedTotal > walletBalance) {
        const short = round2(projectedTotal - walletBalance);
        setScanFeedback({
          type: 'warning',
          message: `Adding "${product.name}" would exceed the wallet balance by ${formatCurrency(short)}.`,
        });
        pushSnackbar(
          `Insufficient wallet balance — short by ${formatCurrency(short)}.`,
          'warning'
        );
        return prev;
      }

      if (existing) {
        setScanFeedback({ type: 'success', message: `Updated: ${product.name} × ${nextQty}` });
        return prev.map((l) => (l.key === existing.key ? { ...l, quantity: nextQty } : l));
      }

      const line = {
        key: `${product.id}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        product_id: product.id,
        product_name: product.name,
        product_code: product.code,
        product_barcode: product.barcode,
        product_category: product.category,
        unit_price: unitPrice,
        tax_rate: 0,
        tax_included: false,
        quantity: qty,
        line_discount: 0,
        max_qty: avail,
      };
      setScanFeedback({ type: 'success', message: `Added: ${product.name}` });
      return [...prev, line];
    });
  }, [walletInfo, walletLocked, walletBalance, pushSnackbar]);

  const onQtyChange = useCallback((key, qty) => {
    setCart((prev) => {
      const line = prev.find((l) => l.key === key);
      if (!line) return prev;

      const cappedByStock = Math.max(1, Math.min(safeNumber(qty, 1), line.max_qty ?? 999999));

      const others = prev.filter((l) => l.key !== key);
      const othersTotal = computeTotals(others).total;
      const lineTotal = round2(cappedByStock * line.unit_price - safeNumber(line.line_discount, 0));
      const projected = round2(othersTotal + lineTotal);

      if (projected > walletBalance) {
        const short = round2(projected - walletBalance);
        const maxQtyForBudget = line.unit_price > 0
          ? Math.max(1, Math.floor((walletBalance - othersTotal + safeNumber(line.line_discount, 0)) / line.unit_price))
          : cappedByStock;
        const finalQty = Math.max(1, Math.min(cappedByStock, maxQtyForBudget));
        if (finalQty === line.quantity) {
          pushSnackbar(`Would exceed wallet by ${formatCurrency(short)}.`, 'warning');
          return prev;
        }
        pushSnackbar(`Quantity capped to ${finalQty} — wallet limit.`, 'warning');
        return prev.map((l) => l.key === key ? { ...l, quantity: finalQty } : l);
      }

      return prev.map((l) => l.key === key ? { ...l, quantity: cappedByStock } : l);
    });
  }, [walletBalance, pushSnackbar]);

  const onRemove = useCallback((key) => setCart((prev) => prev.filter((l) => l.key !== key)), []);

  const onLineDiscount = useCallback((key, val) => {
    setCart((prev) => {
      const line = prev.find((l) => l.key === key);
      if (!line) return prev;
      const gross = line.unit_price * line.quantity;
      const desiredDisc = Math.min(Math.max(0, safeNumber(val, 0)), gross);

      const others = prev.filter((l) => l.key !== key);
      const othersTotal = computeTotals(others).total;
      const lineTotal = round2(gross - desiredDisc);
      const projected = round2(othersTotal + lineTotal);

      if (projected > walletBalance) {
        const short = round2(projected - walletBalance);
        pushSnackbar(`Discount rejected — would still exceed wallet by ${formatCurrency(short)}.`, 'warning');
        return prev;
      }

      return prev.map((l) => l.key === key ? { ...l, line_discount: desiredDisc } : l);
    });
  }, [walletBalance, pushSnackbar]);

  const clearCart = useCallback(() => { setCart([]); setScanFeedback(null); }, []);

  const holdSale = useCallback(() => {
    if (cart.length === 0) return;
    setHeldSales((prev) => [...prev, { id: Date.now(), at: new Date(), cart }]);
    setCart([]);
    pushSnackbar('Sale held.', 'info');
    safeFocusBarcode(100);
  }, [cart, pushSnackbar, safeFocusBarcode]);

  const resumeSale = useCallback(() => {
    if (heldSales.length === 0) return;
    const last = heldSales[heldSales.length - 1];
    const resumedTotal = computeTotals(last.cart).total;
    if (resumedTotal > walletBalance) {
      pushSnackbar(
        `Cannot resume — held sale total ${formatCurrency(resumedTotal)} exceeds wallet balance ${formatCurrency(walletBalance)}.`,
        'error'
      );
      return;
    }
    setCart(last.cart);
    setHeldSales((prev) => prev.slice(0, -1));
    pushSnackbar('Held sale resumed.', 'info');
  }, [heldSales, walletBalance, pushSnackbar]);

  const findByBarcode = useCallback((code) => {
    const c = normalizeBarcode(code);
    if (!c) return null;
    return (
      products.find((p) => normalizeBarcode(p.barcode) === c) ||
      products.find((p) => normalizeBarcode(p.code) === c) ||
      null
    );
  }, [products]);

  const resolveAndAdd = useCallback((rawCode) => {
    const code = normalizeBarcode(rawCode);
    if (!code) return;
    const match = findByBarcode(code);
    if (match) {
      addProductToCart(match, 1);
    } else {
      setScanFeedback({ type: 'error', message: `No product matches "${code}".` });
      pushSnackbar(`No product matches "${code}".`, 'error');
    }
    safeFocusBarcode(60);
  }, [findByBarcode, addProductToCart, pushSnackbar, safeFocusBarcode]);

  const handleBarcodeSubmit = useCallback((e) => {
    if (e) e.preventDefault();
    const code = normalizeBarcode(barcodeValue);
    if (!code) return;
    const now = Date.now();
    if (lastAddRef.current.barcode === code && now - lastAddRef.current.ts < 300) {
      setBarcodeValue('');
      return;
    }
    lastAddRef.current = { barcode: code, ts: now };
    setBarcodeValue('');
    resolveAndAdd(code);
  }, [barcodeValue, resolveAndAdd]);

  const handleScannerDetected = useCallback((code) => {
    setScannerOpen(false);
    const clean = normalizeBarcode(code);
    if (!clean) return;
    resolveAndAdd(clean);
  }, [resolveAndAdd]);

  const submitSale = useCallback(async () => {
    if (!walletInfo) return;
    if (cart.length === 0) return;
    if (walletLocked) { pushSnackbar('Wallet is locked.', 'error'); return; }
    if (cartTotal > walletBalance) {
      pushSnackbar('Cart total exceeds wallet balance.', 'error');
      return;
    }

    setSubmitting(true);

    const payload = {
      items: cart.map((l) => ({
        product_id: l.product_id,
        quantity: l.quantity,
        unit_price: toMoneyString(l.unit_price),
        line_discount: toMoneyString(l.line_discount || 0),
      })),
      payment_method: 'wallet',
      amount_tendered: toMoneyString(cartTotal),
      payer_staff_id: walletInfo.user?.staff_id || '',
      customer_name: null,
      customer_phone: null,
      note: null,
      payment_breakdown: null,
    };

    const res = await apiCreateSale(payload);

    if (!isMountedRef.current) return;
    setSubmitting(false);

    if (!res.success) {
      pushSnackbar(res.error || 'Failed to complete sale', 'error');
      return;
    }

    setChargeOpen(false);
    setReceipt(res.sale);
    setCart([]);

    pushSnackbar(
      `✅ Sale ${res.sale.reference} completed · wallet balance now ${formatCurrency(res.sale.wallet_balance_after ?? 0)}`,
      'success'
    );

    fetchProducts({ silent: true });
    fetchTodayStats();
    refreshWallet();
  }, [
    walletInfo, cart, walletLocked, cartTotal, walletBalance,
    pushSnackbar, fetchProducts, fetchTodayStats, refreshWallet,
  ]);

  useEffect(() => {
    if (!walletInfo) return undefined;
    const onKey = (e) => {
      const tag = e.target && e.target.tagName;
      const inField = tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
      const hasMod = e.ctrlKey || e.metaKey;

      if (e.key === 'F2') { e.preventDefault(); setSearchOpen(true); return; }
      if (e.key === 'F9') {
        e.preventDefault();
        if (cart.length > 0 && !overBudget) setChargeOpen(true);
        return;
      }
      if (e.key === 'F8') { e.preventDefault(); holdSale(); return; }
      if (e.key === 'Escape') {
        setSearchOpen(false); setScannerOpen(false); setCalcOpen(false); setChargeOpen(false);
        return;
      }
      if (hasMod && e.key.toLowerCase() === 'b') {
        e.preventDefault();
        if (e.shiftKey) setScannerOpen(true);
        else barcodeInputRef.current?.focus();
        return;
      }
      if (!inField && !hasMod && e.key.length === 1 && /[0-9]/.test(e.key)) {
        barcodeInputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [walletInfo, cart.length, overBudget, holdSale]);

  useEffect(() => {
    if (!walletInfo) return undefined;
    if (searchOpen || scannerOpen || chargeOpen || receipt || calcOpen) return undefined;
    const t = setTimeout(() => {
      if (!isMountedRef.current) return;
      barcodeInputRef.current?.focus();
    }, 150);
    return () => clearTimeout(t);
  }, [walletInfo, searchOpen, scannerOpen, chargeOpen, receipt, calcOpen]);

  return (
    <Suspense fallback={<div>Loading...</div>}>
      <div className="epos-wrapper">
        <div className="epos-main-layout">
          <Sidebar activeMenu={activeMenu} setActiveMenu={setActiveMenu} />
          <div className="epos-main">
            <Header />
            <div
              className="epos-content"
              style={{
                minHeight: 'calc(100vh - 140px)',
                maxHeight: 'calc(100vh - 140px)',
                overflowY: 'auto',
                overflowX: 'hidden',
              }}
            >
              {!walletInfo ? (
                <StaffWalletGate onVerified={startSession} />
              ) : (
                <Grid
                  container
                  spacing={2}
                  sx={{ minHeight: '100%', alignItems: 'stretch', flexWrap: { xs: 'wrap', md: 'nowrap' } }}
                >
                  <Grid item xs={12} md={8} sx={{ minHeight: 0, display: 'flex' }}>
                    <Stack spacing={2} sx={{ flex: 1, minHeight: 0, width: '100%' }}>
                      <div className="hero-card">
                        <div>
                          <div className="hero-title" style={{ color: ACCE_BRAND.orange, fontWeight: 'bold', fontSize: '20px' }}>
                            {TUCKSHOP.icon} {TUCKSHOP.name}
                          </div>
                          {todayStats && (
                            <div style={{ display: 'flex', gap: 16, marginTop: 6, fontSize: 13, color: '#666' }}>
                              <span><strong>{todayStats.sales_count}</strong> sales</span>
                              <span>Revenue: <strong>{formatCurrency(todayStats.gross_total)}</strong></span>
                              <span>Items: <strong>{todayStats.items_sold}</strong></span>
                            </div>
                          )}
                        </div>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="Reload products">
                            <span>
                              <IconButton onClick={() => fetchProducts()} disabled={productsLoading}
                                sx={{ border: `1px solid ${alpha(ACCE_BRAND.navy, 0.25)}` }}>
                                <RefreshIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Button variant="outlined" onClick={() => setScannerOpen(true)} startIcon={<QrCodeScannerIcon />}
                            sx={{ borderColor: ACCE_BRAND.navy, color: ACCE_BRAND.navy, textTransform: 'none' }}>
                            Camera
                          </Button>
                          <Button variant="outlined" onClick={() => setSearchOpen(true)} startIcon={<SearchIcon />}
                            sx={{ borderColor: ACCE_BRAND.navy, color: ACCE_BRAND.navy, textTransform: 'none' }}>
                            Search (F2)
                          </Button>
                        </Box>
                      </div>

                      <WalletBanner
                        walletInfo={walletInfo}
                        runningTotal={cartTotal}
                        onChangePayer={endSession}
                      />

                      {productsLoading && <LinearProgress sx={{ borderRadius: 1 }} />}

                      <Paper elevation={3}
                        sx={{
                          p: 2, borderRadius: 2,
                          border: `2px solid ${alpha(ACCE_BRAND.orange, 0.35)}`,
                          '&:focus-within': { borderColor: ACCE_BRAND.orange, boxShadow: `0 0 0 3px ${alpha(ACCE_BRAND.orange, 0.15)}` },
                        }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <QrCodeScannerIcon sx={{ color: ACCE_BRAND.orange }} />
                          <Typography variant="subtitle2" fontWeight={800}
                            sx={{ color: ACCE_BRAND.orange, letterSpacing: 0.6, textTransform: 'uppercase', fontSize: '0.75rem' }}>
                            Scan or Enter Barcode
                          </Typography>
                          <Chip label="Ctrl+B" size="small"
                            sx={{ ml: 'auto', height: 18, fontSize: '0.65rem', bgcolor: alpha(ACCE_BRAND.orange, 0.1), color: ACCE_BRAND.orange, fontWeight: 700 }} />
                        </Box>
                        <Box component="form" onSubmit={handleBarcodeSubmit} sx={{ display: 'flex', gap: 1 }}>
                          <TextField inputRef={barcodeInputRef} fullWidth autoFocus
                            value={barcodeValue} onChange={(e) => setBarcodeValue(e.target.value)}
                            placeholder="Scan barcode — press Enter…"
                            InputProps={{
                              startAdornment: (<InputAdornment position="start"><QrCodeScannerIcon sx={{ color: ACCE_BRAND.orange, fontSize: 26 }} /></InputAdornment>),
                              endAdornment: barcodeValue && (
                                <InputAdornment position="end">
                                  <IconButton size="small" onClick={() => setBarcodeValue('')} aria-label="Clear">
                                    <ClearIcon fontSize="small" />
                                  </IconButton>
                                </InputAdornment>
                              ),
                              sx: { fontSize: '1.15rem', fontWeight: 600, letterSpacing: 0.5, bgcolor: '#fff', borderRadius: 2, '& input': { py: 1.5 } },
                            }} />
                          <Button type="submit" variant="contained" disabled={!barcodeValue.trim()} startIcon={<AddIcon />}
                            sx={{ ...glossyButton(ACCE_BRAND.orange, ACCE_BRAND.orangeDark, ACCE_BRAND.orangeLight), px: 3, borderRadius: 2, textTransform: 'none', fontWeight: 700 }}>
                            Add
                          </Button>
                        </Box>
                        {scanFeedback && (
                          <Alert severity={scanFeedback.type} sx={{ mt: 1.5 }} onClose={() => setScanFeedback(null)}>
                            {scanFeedback.message}
                          </Alert>
                        )}
                      </Paper>

                      <Grid container spacing={2} sx={{ flex: 1, minHeight: 0 }}>
                        <Grid item xs={12} sm={6}>
                          <Paper sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Tools</Typography>
                            <Stack spacing={1}>
                              <Button fullWidth variant="outlined" startIcon={<CalculateIcon />} onClick={() => setCalcOpen(true)}
                                sx={{ justifyContent: 'flex-start', textTransform: 'none' }}>
                                Calculator
                              </Button>
                              <Button fullWidth variant="outlined" startIcon={<HoldIcon />} onClick={holdSale}
                                disabled={cart.length === 0}
                                sx={{ justifyContent: 'flex-start', textTransform: 'none' }}>
                                Hold Sale (F8)
                              </Button>
                              <Button fullWidth variant="outlined" startIcon={<ResumeIcon />} onClick={resumeSale}
                                disabled={heldSales.length === 0}
                                sx={{ justifyContent: 'flex-start', textTransform: 'none' }}>
                                Resume ({heldSales.length})
                              </Button>
                            </Stack>
                          </Paper>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Paper sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Shortcuts</Typography>
                            <Stack spacing={0.5}>
                              <Shortcut label="Focus barcode" keys="Ctrl+B" />
                              <Shortcut label="Open camera" keys="Ctrl+Shift+B" />
                              <Shortcut label="Find product" keys="F2" />
                              <Shortcut label="Hold sale" keys="F8" />
                              <Shortcut label="Charge" keys="F9" />
                            </Stack>
                          </Paper>
                        </Grid>
                      </Grid>
                    </Stack>
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    md={4}
                    sx={{
                      height: '100%',
                      minHeight: 0,
                      display: 'flex',
                      position: { md: 'sticky' },
                      top: { md: 0 },
                      alignSelf: 'flex-start',
                    }}
                  >
                    <OrderTray
                      cart={cart}
                      onQtyChange={onQtyChange}
                      onRemove={onRemove}
                      onLineDiscount={onLineDiscount}
                      onClear={clearCart}
                      onHold={holdSale}
                      onResume={resumeSale}
                      heldCount={heldSales.length}
                      onCharge={() => setChargeOpen(true)}
                      onOpenSearch={() => setSearchOpen(true)}
                      onOpenCalculator={() => setCalcOpen(true)}
                      submitting={submitting}
                      walletInfo={walletInfo}
                      onChangePayer={endSession}
                    />
                  </Grid>
                </Grid>
              )}
            </div>
          </div>
        </div>
        <Footer />
      </div>

      <Calculator
        open={calcOpen}
        onClose={() => setCalcOpen(false)}
        onSendToTender={() => { setCalcOpen(false); }}
      />

      <BarcodeScannerDialog
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onDetected={handleScannerDetected}
      />

      <ProductSearchDialog
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        products={products}
        loading={productsLoading}
        disabled={!walletInfo}
        onPick={(p) => { setSearchOpen(false); addProductToCart(p, 1); safeFocusBarcode(80); }}
      />

      <ChargeDialog
        open={chargeOpen}
        onClose={() => setChargeOpen(false)}
        cart={cart}
        walletInfo={walletInfo}
        onConfirm={submitSale}
        submitting={submitting}
      />

      <ReceiptDialog
        open={!!receipt}
        sale={receipt}
        locationName={TUCKSHOP.name}
        onClose={() => { setReceipt(null); safeFocusBarcode(100); }}
        onNewSale={() => { setReceipt(null); safeFocusBarcode(100); }}
        onExitSession={() => { setReceipt(null); endSession(); }}
      />

      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} TransitionComponent={Fade}>
        <Alert onClose={closeSnackbar} severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Suspense>
  );
};

export default SalesTerminal;